import { useMemo, useState } from 'react'
import { useTranslation } from '../../i18n'
import { COLORS } from '../../lib/theme'
import { useTeamRoleLabel } from '../../lib/roles'
import { Icon } from '../Icon'
import { PlanHuntAvatar } from './PlanHuntAvatar'
import { assignStandsInOrder } from './shuffleAssign'
import type { MapObject } from '../../lib/types'
import type { PlanHuntStand, PlanHuntMember } from './types'

export function PlanHuntShuffleAssignModal({
  stands,
  teamPins,
  participants,
  onCancel,
  onApply,
}: {
  stands: PlanHuntStand[];
  teamPins: MapObject[];
  participants: PlanHuntMember[];
  onCancel: () => void;
  onApply: (s: PlanHuntStand[]) => void;
}) {
  const { t } = useTranslation('planHuntDrives');
  const roleLabel = useTeamRoleLabel();
  const [included, setIncluded] = useState<Set<string>>(() => new Set(participants.map((p) => p.id)));
  const [keepExisting, setKeepExisting] = useState(false);
  const [preview, setPreview] = useState<PlanHuntStand[]>(stands);

  const toggleParticipant = (id: string) => {
    setIncluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const reshuffle = useMemo(
    () => () => {
      // Shuffled ids of the included hunters, in assignment order.
      const orderedPool = participants
        .filter((p) => included.has(p.id))
        .map((p) => p.id)
        .sort(() => Math.random() - 0.5);
      // 1:1 matching — each hunter at most one stand; surplus on either side
      // is left over (extra hunters unassigned / extra stands empty).
      setPreview(assignStandsInOrder(stands, orderedPool, keepExisting));
    },
    [stands, participants, included, keepExisting]
  );

  useMemo(() => reshuffle(), [reshuffle]);

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,30,22,0.55)',
        zIndex: 1500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="wh-plan-shuffle"
        style={{
          background: COLORS.surfaceContainerLowest,
          borderRadius: 14,
          width: '100%',
          maxWidth: 720,
          // A definite height (not max-height) is what lets the two columns'
          // inner lists scroll: an auto-sized grid row under a max-height-only
          // box stays content-sized, so a long hunter pool overgrows the box
          // and the footer (Cancel / Apply assignments) gets clipped by
          // `overflow: hidden`. The sibling drive editor uses the same pattern.
          height: 'min(720px, calc(100vh - 60px))',
          boxShadow: '0 24px 72px rgba(0,0,0,0.34)',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Left: pool */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderRight: `1px solid ${COLORS.surfaceContainerHighest}`,
            minHeight: 0,
          }}
        >
          <div style={{ padding: '14px 16px', borderBottom: `1px solid ${COLORS.surfaceContainerHighest}` }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>{t('shuffle.title')}</div>
            <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>
              {t('shuffle.poolHint')}
            </div>
          </div>
          {/* Sticky All / None bar — never scrolls away */}
          <div style={{ padding: '6px 8px', borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`, display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              className="wh-btn wh-btn-outline"
              style={{ height: 28, padding: '0 10px', fontSize: 12 }}
              onClick={() => setIncluded(new Set(participants.map((p) => p.id)))}
            >
              {t('shuffle.all')}
            </button>
            <button
              className="wh-btn wh-btn-outline"
              style={{ height: 28, padding: '0 10px', fontSize: 12 }}
              onClick={() => setIncluded(new Set())}
            >
              {t('shuffle.none')}
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {participants.map((p) => {
              const on = included.has(p.id);
              return (
                <label
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '6px 8px',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleParticipant(p.id)}
                    className="wh-checkbox"
                  />
                  <div style={{ opacity: on ? 1 : 0.4 }}>
                    <PlanHuntAvatar member={p} size={28} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, opacity: on ? 1 : 0.5 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.outline }}>{p.role && roleLabel(p.role)}</div>
                  </div>
                </label>
              );
            })}
          </div>
          <div style={{ padding: '10px 14px', borderTop: `1px solid ${COLORS.surfaceContainerHighest}` }}>
            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: COLORS.onSurface }}
            >
              <input
                type="checkbox"
                checked={keepExisting}
                onChange={(e) => setKeepExisting(e.target.checked)}
                className="wh-checkbox"
              />
              {t('shuffle.keepExisting')}
            </label>
          </div>
        </div>

        {/* Right: preview */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div
            style={{
              padding: '14px 16px',
              borderBottom: `1px solid ${COLORS.surfaceContainerHighest}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.onSurface }}>{t('shuffle.preview')}</div>
              <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>
                {t('shuffle.standCount', { count: preview.length })} · {t('shuffle.hunterCount', { count: included.size })}
              </div>
            </div>
            <button
              className="wh-btn wh-btn-outline"
              style={{ height: 30, padding: '0 12px', fontSize: 12 }}
              onClick={reshuffle}
              disabled={included.size === 0}
            >
              <Icon name="shuffle" size={13} />
              {t('shuffle.reroll')}
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {preview.map((s, i) => {
              const pin = teamPins.find((p) => p.id === s.pinId);
              const a = participants.find((p) => p.id === s.assignedId);
              return (
                <div
                  key={s.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${COLORS.surfaceContainerHighest}`,
                    marginBottom: 4,
                    background: COLORS.surfaceContainerLowest,
                  }}
                >
                  <div
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      flexShrink: 0,
                      background: COLORS.primary,
                      color: COLORS.onPrimary,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pin ? pin.label : t('shuffle.standFallback')}
                    </div>
                  </div>
                  {a ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '3px 8px 3px 3px',
                        borderRadius: 999,
                        border: `1px solid ${COLORS.surfaceContainerHighest}`,
                        background: COLORS.surfaceContainerLowest,
                      }}
                    >
                      <PlanHuntAvatar member={a} size={22} />
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{a.name.split(' ')[0]}</span>
                    </div>
                  ) : (
                    <span style={{ fontSize: 11, color: COLORS.outline, fontStyle: 'italic' }}>—</span>
                  )}
                </div>
              );
            })}
          </div>

          <div
            style={{
              padding: '12px 14px',
              borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
              display: 'flex',
              gap: 8,
              background: COLORS.surface,
            }}
          >
            <button className="wh-btn wh-btn-text" onClick={onCancel}>
              {t('shuffle.cancel')}
            </button>
            <button
              className="wh-btn wh-btn-primary"
              style={{ marginLeft: 'auto' }}
              onClick={() => onApply(preview)}
              disabled={included.size === 0}
            >
              {t('shuffle.applyAssignments')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
